import React, { useState } from 'react';
import SectionBasePage from 'components/SectionBasePage';
import { useDispatch } from 'hooks/store';
import { Tabs } from 'UI';
import { Form } from 'antd';
import { createAlertNotification } from 'infra/notification';
import { AlertNotificationType, setNotificationAction } from 'state/slices/notification';

import { unwrapResult } from '@reduxjs/toolkit';
import { InventoryEntryTypeEnum, InventoryTypeEnum, MenuEntryType } from 'constants/inventory';
import {
  CreateClientVarietyInputData,
  CreateClientVarietyOutputData,
} from 'api/client';
import {
  createClientVarietyInputAction,
  createClientVarietyOutputAction,
  createClientVarietyTransferAction,
} from 'state/slices/clients';
import { createOrganizationVarietyTransferAction } from 'state/slices/organizations';

import EntryInputModal, { EntryInputFormData } from './components/EntryInputModal';
import EntryOutputModal, { EntryOutputFormData } from './components/EntryOutputModal';
import EntryTransferModal, { EntryTransferFormData } from './components/EntryTransferModal';
import Extra from './components/Extra';
import InventoryView from './components/InventoryView';
import EntriesView from './components/EntriesView';

const getFormQuantityAndPrice = (
  formQuantity: string,
  formPrice: string,
): [number, number] => {
  const parsedFormPrice = parseFloat(formPrice);
  const parsedFormQuantity = parseFloat(formQuantity);
  const price = Math.round((parsedFormPrice * 100));
  return [parsedFormQuantity, price];
};

enum TabKeys {
  INVENTORY = 'INVENTORY',
  ENTRIES = 'ENTRIES',
}

const ClientTrayPage = (): JSX.Element => {
  const dispatch = useDispatch();

  const [entryInputForm] = Form.useForm<EntryInputFormData>();
  const [entryOutputForm] = Form.useForm<EntryOutputFormData>();
  const [entryTransferForm] = Form.useForm<EntryTransferFormData>();

  const [isExtraPopoverVisible, setExtraPopoverVisible] = useState(false);
  const [isEntryInputModalOpen, setEntryInputModalOpen] = useState(false);
  const [isEntryOutputModalOpen, setEntryOutputModalOpen] = useState(false);
  const [isEntryTransferModalOpen, setEntryTransferModalOpen] = useState(false);

  const [isLoading, setLoading] = useState(false);
  const [shouldReload, setReload] = useState(false);

  const openEntryInputModal = () => {
    setEntryInputModalOpen(true);
  };

  const openEntryOutputModal = () => {
    setEntryOutputModalOpen(true);
  };

  const openEntryTransferModal = () => {
    setEntryTransferModalOpen(true);
  };

  const openEntryModal = (entryType?: MenuEntryType) => {
    setExtraPopoverVisible(false);
    const entryModalMapping: Record<MenuEntryType, () => void> = {
      [InventoryEntryTypeEnum.INPUT]: openEntryInputModal,
      [InventoryEntryTypeEnum.OUTPUT]: openEntryOutputModal,
      [InventoryEntryTypeEnum.TRANSFER]: openEntryTransferModal,
    };

    if (entryType) {
      entryModalMapping[entryType]();
    }
  };

  const closeEntryInputModal = () => {
    setEntryInputModalOpen(false);
    entryInputForm.resetFields();
  };

  const closeEntryOutputModal = () => {
    setEntryOutputModalOpen(false);
    entryOutputForm.resetFields();
  };

  const closeEntryTransferModal = () => {
    setEntryTransferModalOpen(false);
    entryTransferForm.resetFields();
  };

  const submitEntryInputForm = async () => {
    try {
      await entryInputForm.validateFields();
    } catch (e) {
      const errorAlert = createAlertNotification(
        'Verifique os campos com erro.',
        AlertNotificationType.Error,
      );
      dispatch(setNotificationAction(errorAlert));
      return;
    }

    const formData: EntryInputFormData = entryInputForm.getFieldsValue();

    const parsedItems = formData.items.map((entryItem) => {
      const [quantity, price] = getFormQuantityAndPrice(
        entryItem.quantity,
        entryItem.totalPrice,
      );
      return {
        varietyId: entryItem.variety,
        batch: entryItem.batch,
        expiryDate: entryItem.expiryDate.toISOString(),
        germinationRate: parseInt(entryItem.germinationRate, 10) / 100.0,
        quantity,
        price,
      };
    });

    const entryInputData: CreateClientVarietyInputData = {
      clientId: formData.client,
      data: {
        entryDate: formData.entryDate.toISOString(),
        provider: formData.provider,
        receivingTermCode: formData.receivingTermCode,
        deliveredBy: formData.deliveredBy,
        receivedBy: formData.receivedBy,
        fiscalNote: formData.fiscalNote,
        notes: formData.notes,
        items: parsedItems,
      },
    };

    try {
      setLoading(true);
      const result = await dispatch(createClientVarietyInputAction(entryInputData));
      unwrapResult(result);
      const successAlert = createAlertNotification(`
      Entrada de semente criada com sucesso.
      `, AlertNotificationType.Success);
      dispatch(setNotificationAction(successAlert));
      setLoading(false);
      closeEntryInputModal();
      setReload(true);
    } catch (err) {
      const errorAlert = createAlertNotification(
        'Erro interno ao tentar criar entrada de semente. Tente novamente.',
        AlertNotificationType.Error,
      );
      dispatch(setNotificationAction(errorAlert));
      setLoading(false);
    }
  };

  const submitEntryOutputForm = async () => {
    try {
      await entryOutputForm.validateFields();
    } catch (e) {
      const errorAlert = createAlertNotification(
        'Verifique os campos com erro.',
        AlertNotificationType.Error,
      );
      dispatch(setNotificationAction(errorAlert));
      return;
    }

    const formData: EntryOutputFormData = entryOutputForm.getFieldsValue();

    const parsedItems = formData.items.map((entryItem) => {
      const parsedFormQuantity = parseFloat(entryItem.quantity);
      return {
        varietyId: entryItem.variety,
        batchId: entryItem.batchId,
        quantity: parsedFormQuantity,
      };
    });

    const entryOutputData: CreateClientVarietyOutputData = {
      clientId: formData.client,
      data: {
        entryDate: formData.entryDate.toISOString(),
        costCenter: formData.costCenter,
        outputType: formData.outputType,
        notes: formData.notes,
        items: parsedItems,
      },
    };

    try {
      setLoading(true);
      const result = await dispatch(createClientVarietyOutputAction(entryOutputData));
      unwrapResult(result);
      const successAlert = createAlertNotification(`
      Saída de semente criada com sucesso.
      `, AlertNotificationType.Success);
      dispatch(setNotificationAction(successAlert));
      setLoading(false);
      closeEntryOutputModal();
      setReload(true);
    } catch (err) {
      // TODO: check error type to see if we should display a specific message
      // Ex: insufficient quantity to withdraw
      const errorAlert = createAlertNotification(
        'Erro interno ao tentar criar saída de semente. Tente novamente.',
        AlertNotificationType.Error,
      );
      dispatch(setNotificationAction(errorAlert));
      setLoading(false);
    }
  };

  const submitEntryTransferForm = async () => {
    try {
      await entryTransferForm.validateFields();
    } catch (e) {
      const errorAlert = createAlertNotification(
        'Verifique os campos com erro.',
        AlertNotificationType.Error,
      );
      dispatch(setNotificationAction(errorAlert));
      return;
    }

    const formData: EntryTransferFormData = entryTransferForm.getFieldsValue();

    const parsedItems = formData.items.map((entryItem) => {
      const [quantity, price] = getFormQuantityAndPrice(
        entryItem.quantity,
        entryItem.totalPrice,
      );
      return {
        varietyId: entryItem.variety,
        batchId: entryItem.batchId,
        quantity,
        price,
      };
    });

    const data = {
      entryDate: formData.entryDate.toISOString(),
      source: formData.client,
      sourceType: InventoryTypeEnum.CLIENT,
      notes: formData.notes,
      items: parsedItems,
    };

    const orgAction = createOrganizationVarietyTransferAction({
      organizationId: formData.destinationOrganization,
      data,
    });

    const clientAction = createClientVarietyTransferAction({
      clientId: formData.destinationClient,
      data,
    });

    try {
      setLoading(true);
      let result;
      if (formData.destinationType === InventoryTypeEnum.ORGANIZATION) {
        result = await dispatch(orgAction);
      } else {
        result = await dispatch(clientAction);
      }
      unwrapResult(result);
      const successAlert = createAlertNotification(`
      Semente transferida com sucesso.
      `, AlertNotificationType.Success);
      dispatch(setNotificationAction(successAlert));
      setLoading(false);
      closeEntryTransferModal();
      setReload(true);
    } catch (err) {
      // TODO: check error type to see if we should display a specific message
      // Ex: insufficient quantity to withdraw
      const errorAlert = createAlertNotification(
        'Erro interno ao tentar transferir semente. Tente novamente.',
        AlertNotificationType.Error,
      );
      dispatch(setNotificationAction(errorAlert));
      setLoading(false);
    }
  };

  const onReloadFinish = () => {
    setReload(false);
  };

  return (
    <SectionBasePage>
      <>
        <Tabs
          tabBarExtraContent={(
            <Extra
              onVisibleChange={(isVisible) => setExtraPopoverVisible(isVisible)}
              onEntryClick={(type?: MenuEntryType) => openEntryModal(type)}
              isPopoverVisible={isExtraPopoverVisible}
            />
          )}
        >
          <Tabs.TabPane tab="Estoque atual" key={TabKeys.INVENTORY}>
            <InventoryView shouldReload={shouldReload} onReloadFinish={onReloadFinish} />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Transações" key={TabKeys.ENTRIES}>
            <EntriesView shouldReload={shouldReload} onReloadFinish={onReloadFinish} />
          </Tabs.TabPane>
        </Tabs>
        <EntryInputModal
          formHandler={entryInputForm}
          onSubmit={submitEntryInputForm}
          onCancel={closeEntryInputModal}
          isLoading={isLoading}
          isVisible={isEntryInputModalOpen}
        />
        <EntryOutputModal
          formHandler={entryOutputForm}
          onSubmit={submitEntryOutputForm}
          onCancel={closeEntryOutputModal}
          isLoading={isLoading}
          isVisible={isEntryOutputModalOpen}
        />
        <EntryTransferModal
          formHandler={entryTransferForm}
          onSubmit={submitEntryTransferForm}
          onCancel={closeEntryTransferModal}
          isLoading={isLoading}
          isVisible={isEntryTransferModalOpen}
        />
      </>
    </SectionBasePage>
  );
};

export default ClientTrayPage;
